// @ts-nocheck
import { formGlobals } from './_/form-globals';
import BaseInput from './_/base-input';

/**
 * Class: PhoneInput
 * Purpose: Manage a single <input type="tel">, track phone validity and emit custom events on changes.
 * Constructor Parameters (types):
 *   wrap (HTMLElement)      - Container element that wraps the input + error UI.
 *   formIndex (number|null) - Index of the parent form instance.
 *   wrapIndex (number|null) - Index of this field within the form instance.
 *
 * Public Methods:
 *   isValid(): boolean
 *     // Returns current validity state.
 *
 *   getState(): { value:string, valid:boolean, blurred:boolean, required:boolean, id:string, digitCount:number }
 *     // Snapshot incl. digit count of current value (digits only).
 *
 *   validateNow(): void
 *     // Forces validation against current value.
 *
 *   destroy(): void
 *     // Removes listeners.
 *
 * Events:
 *   'validchange' (detail:{ valid:boolean, value:string, digitCount:number, instance:PhoneInput })
 *   'valuechange' (detail:{ value:string, digitCount:number, instance:PhoneInput })
 *   'blur'        (detail:{ instance:PhoneInput })
 *   'focus'       (detail:{ instance:PhoneInput })
 *
 * Notes:
 * - Events bubble and are composed so parent controllers can listen without holding references.
 * - data-validate rules supported:
 *     • e164            → must match /^\+[1-9]\d{7,14}$/  (max 15 digits, leading +)
 *     • digitsOnly      → only digits allowed (no spaces, +, hyphens, etc.)
 *     • minDigits:N     → at least N digits
 *     • maxDigits:N     → at most  N digits
 *   Combine with spaces or commas, e.g.: data-validate="e164 minDigits:10 maxDigits:15"
 * 
 * @class
 * @extends {BaseInput}
 * @this {PhoneInput}
 */
export default class PhoneInput extends BaseInput {
  /**
   * @param {HTMLElement} wrap
   * @param {number|null} formIndex
   * @param {number|null} wrapIndex
   */
  constructor(wrap, formIndex = null, wrapIndex = null) {
    super(wrap, formIndex, wrapIndex);

    /** @type {HTMLInputElement|null} */
    this.input = wrap.querySelector('input[type="tel"]');
    this.inputId = `form-${formIndex}-input-${wrapIndex}`;

    // Initialize base class elements
    this._initErrorElement();
    this._initNoteElement(this.wrap.querySelector(formGlobals.noteSelector) || null);

    // config
    this.required    = this.input?.getAttribute('aria-required') === 'true';
    this.validateKey = this.input?.getAttribute('data-validate') || null;

    // state
    this.blurred = false;
    this.value = this.input?.value ?? '';
    this.digitCount = PhoneInput.countDigits(this.value);
    this.valid = this.required ? false : true;

    // listeners cleanup
    this._onFocus = this._onFocus.bind(this);
    this._onBlur  = this._onBlur.bind(this);
    this._onInput = this._onInput.bind(this);

    this._setAttributes();
    this._initEvents();
  }

  /** @returns {HTMLInputElement|null} */
  _getAriaElement() {
    return this.input;
  }

  _getValidChangeDetail(): { valid: boolean; value: string; digitCount: number; instance: PhoneInput } {
    return {
      valid: this.valid,
      value: this.value,
      digitCount: this.digitCount,
      instance: this
    };
  }

  /** @returns {boolean} */
  isValid() { return this.valid; }

  /** @returns {{value:string, valid:boolean, blurred:boolean, required:boolean, id:string, digitCount:number}} */
  getState() {
    return {
      value: this.value,
      valid: this.valid,
      blurred: this.blurred,
      required: this.required,
      id: this.inputId,
      digitCount: this.digitCount
    };
  }

  /** Force validation against current value. */
  validateNow() { this._validate(this.value); }

  /** Remove listeners and references. */
  destroy() {
    super.destroy();
    this.input = null;
  }

  // ---------- Private ----------
  /**
   * Function: _setAttributes
   * Purpose: Initialize IDs and initial aria-describedby based on note/error presence.
   */
  _setAttributes() {
    if (!this.input) return;

    this.input.setAttribute('id', this.inputId);

    // helpful hints for mobile & autofill
    if (!this.input.hasAttribute('inputmode')) {
      this.input.setAttribute('inputmode', 'tel');
    }
    if (!this.input.hasAttribute('autocomplete')) {
      this.input.setAttribute('autocomplete', 'tel');
    }

    // Initial describedby: note if present
    this._setAriaDescribedBy({ includeNote: true, includeError: false });
  }

  /**
   * Function: _initEvents
   * Purpose: Attach DOM listeners with AbortController for clean teardown.
   */
  _initEvents() {
    if (!this.input) return;
    const o = { signal: this._ac.signal };

    this.input.addEventListener('focus', this._onFocus, o);
    this.input.addEventListener('blur',  this._onBlur,  o);
    this.input.addEventListener('input', this._onInput, o);
  }

  _onFocus() {
    this.wrap.classList.add(formGlobals.focusedClass);
    this._dispatch('focus', { instance: this });
  }

  _onBlur() {
    this.wrap.classList.remove(formGlobals.focusedClass);
    const wasFirst = !this.blurred;
    this.blurred = true;
    this._dispatch('blur', { instance: this });

    if (wasFirst && this.required) {
      this._validate(this.value);
    }
  }

  _onInput() {
    this.value = this.input.value;
    this.digitCount = PhoneInput.countDigits(this.value);

    if (this.value) {
      this.wrap.classList.add(formGlobals.filledClass);
    } else {
      this.wrap.classList.remove(formGlobals.filledClass);
    }

    this._dispatch('valuechange', { value: this.value, digitCount: this.digitCount, instance: this });

    if (this.blurred && (this.required || this.value !== '')) {
      this._validate(this.value);
    }
  }

  /**
   * Function: _validate
   * Purpose: Apply required rule, default loose phone rules, and optional data-validate rules.
   * Params:
   *   raw (string) - current raw value
   */
  _validate(raw) {
    const trimmed = raw.trim();
    const digitCount = PhoneInput.countDigits(trimmed);

    // Required rule
    if (this.required) {
      if (trimmed === '' || digitCount === 0) return this._setInvalid();
    } else {
      if (trimmed === '') return this._setValid();
    }

    // Default loose acceptance:
    // - allow digits, spaces, hyphens, dots, parentheses, leading plus
    // - ensure at least 6 digits (adjust if you prefer)
    const looseOk = PhoneInput.LOOSE_RE.test(trimmed) && digitCount >= 6;
    if (!this.validateKey && !looseOk) return this._setInvalid();

    // data-validate rules (if present) override/augment
    if (this.validateKey) {
      const rules = PhoneInput.parseValidateKey(this.validateKey);
      for (const rule of rules) {
        if (!PhoneInput.applyRule(rule, trimmed, digitCount)) {
          return this._setInvalid();
        }
      }
    }

    return this._setValid();
  }


  // ---------- Static helpers & rules ----------
  /**
   * Function: countDigits
   * Purpose: Count numeric digits in a string.
   * Params:
   *   v (string)
   * Returns:
   *   number
   */
  static countDigits(v) {
    if (!v) return 0;
    const m = String(v).match(/\d/g);
    return m ? m.length : 0;
    }

  /**
   * Allowed characters for loose validation:
   *  - digits, spaces, hyphens, dots, parentheses, plus sign (leading + also fine)
   */
  static LOOSE_RE = /^[+()\d.\-\s]+$/;

  /**
   * Strict E.164 pattern: leading +, then 8..15 digits (country code cannot start with 0).
   * (Most references: max 15 digits; we use min 8 as a pragmatic lower bound.)
   */
  static E164_RE = /^\+[1-9]\d{7,14}$/;

  /**
   * Function: parseValidateKey
   * Purpose: Parse data-validate string into structured rules.
   * Supports: e164 | digitsOnly | minDigits:N | maxDigits:N
   */
  static parseValidateKey(key) {
    const parts = key.split(/[,\s]+/).filter(Boolean);
    return parts.map(p => {
      const [t, argStr] = p.split(':');
      const n = argStr ? parseInt(argStr, 10) : undefined;
      return { type: t, n: Number.isFinite(n) ? n : undefined };
    });
  }

  /**
   * Function: applyRule
   * Purpose: Apply a rule to a phone string.
   * Params:
   *   rule  ({type:string,n?:number})
   *   raw   (string) - raw phone string
   *   digits(number) - digit count
   * Returns:
   *   boolean
   */
  static applyRule(rule, raw, digits) {
    switch (rule.type) {
      case 'e164':
        return PhoneInput.E164_RE.test(raw);
      case 'digitsOnly':
        return /^\d+$/.test(raw);
      case 'minDigits':
        return typeof rule.n === 'number' ? digits >= rule.n : true;
      case 'maxDigits':
        return typeof rule.n === 'number' ? digits <= rule.n : true;
      default:
        return true; // unknown rule: ignore (failsafe)
    }
  }
}
